import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Zap, ArrowUpRight, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

// Display name + suggested next tier (for free/solo/student → upgrade path)
const TIER_LADDER = {
  free:    { name: "Explorer",   nextId: "solo",    nextName: "Solo",       nextPrice: "₹299/mo",   nextPromptsLabel: "15 prompts" },
  solo:    { name: "Solo",       nextId: "student", nextName: "Apprentice", nextPrice: "₹699/mo",   nextPromptsLabel: "30 prompts + Swift→Canvas bridge" },
  student: { name: "Apprentice", nextId: "builder", nextName: "Creator",    nextPrice: "₹1,999/mo", nextPromptsLabel: "250 prompts + 5 marketing screenshots/mo" },
  builder: { name: "Creator",    nextId: "studio",  nextName: "Founder Mode", nextPrice: "₹6,999/mo", nextPromptsLabel: "1,500 prompts + team seats" },
};

export default function UpgradeNudge({ usage, variant = "app" }) {
  const [dismissed, setDismissed] = useState(false);

  if (!usage || usage.unlimited || dismissed) return null;
  const limit = usage.limit || 0;
  const used = usage.used || 0;
  if (limit <= 0) return null;
  const ratio = used / limit;
  if (ratio < 0.8) return null;

  const remaining = Math.max(0, limit - used);
  const overCap = used >= limit;
  const tierKey = usage.tier || "free";
  const tier = TIER_LADDER[tierKey] || TIER_LADDER.free;

  const buyBooster = () => {
    toast("Prompt Booster · 100 prompts for ₹499", {
      description: "Stripe checkout ships next iteration. We'll email hello@durgaprogrammingadventures.com to top you up manually meanwhile.",
      action: {
        label: "Email us",
        onClick: () =>
          window.open(
            "mailto:hello@durgaprogrammingadventures.com?subject=Prompt%20Booster%20top-up&body=Please%20top%20up%20100%20prompts%20on%20my%20account.",
            "_blank"
          ),
      },
    });
  };

  const baseTone = overCap
    ? "border-rose-300 bg-gradient-to-br from-rose-50 via-cream to-paper"
    : "border-saffron bg-gradient-to-br from-saffron/15 via-cream to-paper";
  const Icon = overCap ? AlertCircle : Sparkles;
  const iconTone = overCap ? "text-rose-600" : "text-marigold";

  return (
    <div
      className={`relative rounded-3xl border-2 ${baseTone} p-5 md:p-6 mb-6 shadow-[0_8px_24px_-12px_rgba(31,26,23,0.18)]`}
      data-testid={`upgrade-nudge-${variant}`}
    >
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-3 right-3 w-7 h-7 rounded-full text-ink2 hover:bg-ink/5 flex items-center justify-center transition"
        aria-label="Dismiss"
        data-testid={`upgrade-nudge-dismiss-${variant}`}
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className={`w-12 h-12 rounded-2xl bg-white border-2 ${overCap ? "border-rose-200" : "border-saffron"} flex-shrink-0 flex items-center justify-center ${iconTone}`}>
          <Icon className="w-5 h-5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-bold text-marigold mb-1">
            {overCap ? "Out of prompts" : `Almost out · ${remaining} left`}
          </div>
          <h3 className="font-heading text-xl md:text-2xl font-semibold leading-tight text-ink">
            {overCap ? (
              <>You've used all <span className="tabular-nums">{limit}</span> {tier.name} prompts this month.</>
            ) : (
              <>Only <span className="text-marigold tabular-nums">{remaining}</span> of <span className="tabular-nums">{limit}</span> {tier.name} prompts left.</>
            )}
          </h3>
          <p className="text-sm text-ink2 mt-1">
            Top up with a one-time booster, or upgrade your tier for the rest of the month — whichever feels lighter.
          </p>

          {/* progress bar */}
          <div className="mt-3 h-1.5 rounded-full bg-paper overflow-hidden border border-border">
            <div
              className={`h-full ${overCap ? "bg-rose-500" : "bg-marigold"}`}
              style={{ width: `${Math.min(100, Math.round(ratio * 100))}%` }}
              data-testid={`upgrade-nudge-progress-${variant}`}
            />
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={buyBooster}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-cream text-sm font-semibold hover:-translate-y-0.5 transition"
              data-testid={`upgrade-nudge-booster-${variant}`}
            >
              <Zap className="w-3.5 h-3.5" /> +100 prompts for ₹499
            </button>
            {tier.nextId && (
              <Link
                to="/pricing"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-saffron text-ink text-sm font-semibold border-2 border-ink hover:-translate-y-0.5 transition"
                data-testid={`upgrade-nudge-upgrade-${variant}`}
              >
                Upgrade to {tier.nextName} · {tier.nextPrice}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            )}
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm text-ink2 hover:text-ink transition"
              data-testid={`upgrade-nudge-compare-${variant}`}
            >
              Compare all tiers
            </Link>
          </div>

          {tier.nextPromptsLabel && (
            <div className="text-[11px] text-ink2 mt-3">
              {tier.nextName} unlocks <span className="font-semibold text-ink">{tier.nextPromptsLabel}</span>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
